import { apiClient, ApiError } from './api';
import type {
  BoardCommit, BoardCommitSummary, BoardDto, CardDto, CardUpsertDto, CreateBoardCommitDto, MoveCardDto, StreamDto,
  WorkStreamUpsertDto,
} from './types';

/** Thin wrappers over `apiClient` for the `/api/work` endpoints. */

export const boardQueryKey = ['board'] as const;

export const getBoard = () => apiClient.get<BoardDto>('/api/work/board');

export const createCard = (dto: CardUpsertDto) => apiClient.post<CardDto>('/api/work/cards', dto);

export const updateCard = (id: number, dto: CardUpsertDto) =>
  apiClient.put<CardDto>(`/api/work/cards/${id}`, dto);

/** The update body that re-sends a card unchanged; spread overrides on top for a one-field edit. */
export const upsertFromCard = (card: CardDto): CardUpsertDto => ({
  workStreamId: card.workStreamId,
  title: card.title,
  description: card.description,
  roiOneTime: card.roiOneTime,
  roiAnnual: card.roiAnnual,
  roiBreakdown: card.roiBreakdown,
  roiExplanation: card.roiExplanation,
  workersNeeded: card.workersNeeded,
  estimateWeeks: card.estimateWeeks,
  productOwnerName: card.productOwnerName,
  dependencyCardId: card.dependencyCardId,
  tokensCost: card.tokensCost,
  additionalCost: card.additionalCost,
  targetMonth: card.targetMonth,
});

export const moveCard = (id: number, dto: MoveCardDto) =>
  apiClient.post<void>(`/api/work/cards/${id}/move`, dto);

export const deleteCard = (id: number) => apiClient.del<void>(`/api/work/cards/${id}`);

export const createStream = (dto: WorkStreamUpsertDto) => apiClient.post<StreamDto>('/api/work/streams', dto);

export const updateStream = (id: number, dto: WorkStreamUpsertDto) =>
  apiClient.put<StreamDto>(`/api/work/streams/${id}`, dto);

export const deleteStream = (id: number) => apiClient.del<void>(`/api/work/streams/${id}`);

export const commitsQueryKey = ['commits'] as const;
export const commitQueryKey = (id: number) => ['commit', id] as const;

export const createCommit = (dto: CreateBoardCommitDto) =>
  apiClient.post<BoardCommitSummary>('/api/work/commits', dto);

/** Newest first. */
export const listCommits = () => apiClient.get<BoardCommitSummary[]>('/api/work/commits');

export const getCommit = (id: number) => apiClient.get<BoardCommit>(`/api/work/commits/${id}`);

/** Restores the live board to a commit. Resolves to the safety commit taken just before the restore. */
export const restoreCommit = (id: number) =>
  apiClient.post<BoardCommitSummary>(`/api/work/commits/${id}/restore`, {});

/** The ProblemDetails `detail` (or `title`) of a failed request, else the fallback. */
export function problemDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const body = err.body as { detail?: unknown; title?: unknown };
    if (typeof body.detail === 'string' && body.detail) return body.detail;
    if (typeof body.title === 'string' && body.title) return body.title;
  }
  return fallback;
}

export function isApiStatus(err: unknown, status: number): boolean {
  return err instanceof ApiError && err.status === status;
}
